import type { Cost, Revenue, CostMpkAllocation } from "@shared/schema";

/**
 * Parse Polish date format (DD.MM.YYYY) or ISO format (YYYY-MM-DD) to Date object
 */
export function parsePolishDate(dateStr: string | null | undefined): Date | null {
  if (!dateStr) return null;

  // Try DD.MM.YYYY format (Polish)
  if (dateStr.includes(".")) {
    const parts = dateStr.split(".");
    if (parts.length !== 3) return null;
    const [day, month, year] = parts.map(Number);
    if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
    return new Date(year, month - 1, day);
  }

  // Try YYYY-MM-DD format (ISO)
  if (dateStr.includes("-")) {
    const parts = dateStr.split("-");
    if (parts.length !== 3) return null;
    const [year, month, day] = parts.map(Number);
    if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
    return new Date(year, month - 1, day);
  }

  return null;
}

/**
 * Check if a date is overdue (before today)
 */
export function isOverdue(dateStr: string | null | undefined): boolean {
  const date = parsePolishDate(dateStr);
  if (!date) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);

  return date < today;
}

/**
 * Get all costs allocated to a specific project
 */
export function getCostsForProject(
  projectNr: string,
  costs: Cost[],
  mpkAllocations: CostMpkAllocation[]
): Cost[] {
  // Find all cost IDs allocated to this project
  const costIds = new Set(
    mpkAllocations
      .filter(a => a.mpk === projectNr)
      .map(a => a.costId)
  );

  return costs.filter(c => costIds.has(c.id));
}

/**
 * Get all revenues for a specific project
 */
export function getRevenuesForProject(
  projectNr: string,
  revenues: Revenue[]
): Revenue[] {
  return revenues.filter(r => r.nrPorzadkowyProjektu === projectNr);
}

/**
 * Check if project has any overdue costs (unpaid and past deadline)
 */
export function hasOverdueCosts(
  projectNr: string,
  costs: Cost[],
  mpkAllocations: CostMpkAllocation[]
): boolean {
  const projectCosts = getCostsForProject(projectNr, costs, mpkAllocations);

  return projectCosts.some(c => {
    // If already paid, not overdue
    if (c.zaplacono) return false;
    // Check if deadline has passed
    return isOverdue(c.terminPlatnosci);
  });
}

/**
 * Check if project has any unpaid revenues
 */
export function hasUnpaidRevenues(
  projectNr: string,
  revenues: Revenue[]
): boolean {
  const projectRevenues = getRevenuesForProject(projectNr, revenues);
  return projectRevenues.some(r => r.czyZaplacono === "Nie");
}

/**
 * Get cost payment status for display
 */
export function getCostStatus(cost: Cost): "paid" | "overdue" | "pending" {
  if (cost.zaplacono) return "paid";
  if (isOverdue(cost.terminPlatnosci)) return "overdue";
  return "pending";
}

/**
 * Get revenue payment status for display
 */
export function getRevenueStatus(revenue: Revenue): "paid" | "unpaid" {
  return revenue.czyZaplacono === "Tak" ? "paid" : "unpaid";
}

/**
 * Count costs by status for a project
 */
export function countCostsByStatus(
  projectNr: string,
  costs: Cost[],
  mpkAllocations: CostMpkAllocation[]
): { paid: number; overdue: number; pending: number; total: number } {
  const projectCosts = getCostsForProject(projectNr, costs, mpkAllocations);

  let paid = 0;
  let overdue = 0;
  let pending = 0;

  projectCosts.forEach(c => {
    const status = getCostStatus(c);
    if (status === "paid") paid++;
    else if (status === "overdue") overdue++;
    else pending++;
  });

  return { paid, overdue, pending, total: projectCosts.length };
}

/**
 * Count revenues by status for a project
 */
export function countRevenuesByStatus(
  projectNr: string,
  revenues: Revenue[]
): { paid: number; unpaid: number; total: number } {
  const projectRevenues = getRevenuesForProject(projectNr, revenues);

  let paid = 0;
  let unpaid = 0;

  projectRevenues.forEach(r => {
    if (r.czyZaplacono === "Tak") paid++;
    else unpaid++;
  });

  return { paid, unpaid, total: projectRevenues.length };
}

/**
 * Get total costs amount allocated to a project (uses mpkKwota from allocations)
 */
export function getTotalCostsForProject(
  projectNr: string,
  mpkAllocations: CostMpkAllocation[]
): number {
  return mpkAllocations
    .filter(a => a.mpk === projectNr)
    .reduce((sum, a) => sum + (parseFloat(a.mpkKwota || "0") || 0), 0);
}

/**
 * Get total paid revenue amount for a project
 */
export function getPaidRevenueForProject(
  projectNr: string,
  revenues: Revenue[]
): number {
  return revenues
    .filter(r => r.nrPorzadkowyProjektu === projectNr && r.czyZaplacono === "Tak")
    .reduce((sum, r) => sum + (parseFloat(r.kwotaNetto || "0") || 0), 0);
}

/**
 * Get total revenue amount for a project (all revenues, not just paid)
 */
export function getTotalRevenueForProject(
  projectNr: string,
  revenues: Revenue[]
): number {
  return revenues
    .filter(r => r.nrPorzadkowyProjektu === projectNr)
    .reduce((sum, r) => sum + (parseFloat(r.kwotaNetto || "0") || 0), 0);
}

/**
 * Get profit for a project (paid revenue - total costs)
 */
export function getProfitForProject(
  projectNr: string,
  revenues: Revenue[],
  mpkAllocations: CostMpkAllocation[]
): number {
  const paidRevenue = getPaidRevenueForProject(projectNr, revenues);
  const totalCosts = getTotalCostsForProject(projectNr, mpkAllocations);
  return paidRevenue - totalCosts;
}

/**
 * Get profit margin percentage for a project (profit / revenue * 100)
 * Returns null if there's no revenue to calculate margin from
 */
export function getProfitMarginForProject(
  projectNr: string,
  revenues: Revenue[],
  mpkAllocations: CostMpkAllocation[]
): number | null {
  const paidRevenue = getPaidRevenueForProject(projectNr, revenues);
  if (paidRevenue === 0) return null;

  const profit = getProfitForProject(projectNr, revenues, mpkAllocations);
  return (profit / paidRevenue) * 100;
}
